import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { shiftFindings, shiftSummary } from '@/data/branchFindings';
import { t } from '@/i18n';
import { BranchFrame } from './BranchFrame';

const OUTCOME_STYLE = {
  ejected: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  review: 'border-amber-300 bg-amber-50 text-amber-800',
  false: 'border-slate-300 bg-slate-50 text-slate-700',
};

const Stat = ({ label, value }) => (
  <div className="rounded-md border p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-xl font-bold tabular-nums">{value}</div>
  </div>
);

// Findings of the morning shift (#75, concept): totals, findings per hour, sample frames.
export const ShiftFindings = () => {
  const samples = [...shiftFindings].sort((a, b) => b.confidence - a.confidence).slice(0, 3)
    .concat(shiftFindings.filter(f => f.outcome === 'false').slice(0, 1));
  return (
    <Card className="p-4 sm:p-6 space-y-4">
      <div className="text-sm font-semibold">{t('branchDetection.shift.title')}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label={t('branchDetection.shift.total')} value={shiftSummary.total} />
        <Stat label={t('branchDetection.outcome.ejected')} value={shiftSummary.ejected} />
        <Stat label={t('branchDetection.outcome.review')} value={shiftSummary.review} />
        <Stat label={t('branchDetection.outcome.false')} value={shiftSummary.falseAlarms} />
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-1">{t('branchDetection.shift.perHour')}</div>
        <div className="h-28">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={shiftSummary.perHour} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" name={t('branchDetection.shift.total')} fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {samples.map(f => (
          <figure key={f.id} className="space-y-1">
            <BranchFrame finding={f} label={t('branchDetection.label')} />
            <figcaption className="flex flex-wrap items-center gap-2 text-xs">
              <span className="tabular-nums font-medium">{f.time}</span>
              <span className="text-muted-foreground">{f.lengthCm} cm · {f.confidence} %</span>
              <Badge variant="outline" className={`ml-auto text-[10px] ${OUTCOME_STYLE[f.outcome]}`}>{t(`branchDetection.outcome.${f.outcome}`)}</Badge>
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">{t('branchDetection.shift.note')}</p>
    </Card>
  );
};
